import React, { useCallback, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Card,
    Divider,
    Flex,
    Group,
    NumberInput,
    SegmentedControl,
    SimpleGrid,
    Stack,
    Table,
    Text,
    ThemeIcon,
    Title,
    useComputedColorScheme,
    useMantineTheme,
} from '@mantine/core';
import { IconCalculator, IconRefresh } from '@tabler/icons-react';
import {
    DEFAULT_VENUE_PRICING,
    NET_PROFIT_HORIZON_MONTHS,
    STEADY_STATE_VENUE_PRICING,
    computeVenuePricing,
    type BreakEven,
    type CustomerInvoice,
    type ExpansionInstallTiming,
    type HardwareMode,
    type SoftwareCadence,
    type VenuePricingParams,
    type WatchlyNetProfitPoint,
} from '../../pricing/venuePricing';
import VenueSensitivityExplorer from './VenueSensitivityExplorer';
import {
    feasibleInputSet,
    metricsFromBreakdown,
    sweepActiveVenueParams,
    type FeasibleInputSet,
    type OutputBounds,
    type SweepableVenueParam,
} from '../../pricing/venuePricingSweep';

const currency = (n: number) =>
    n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const currencyDetail = (n: number) =>
    n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

function cloneDefaults(): VenuePricingParams {
    return { ...DEFAULT_VENUE_PRICING };
}

function formatBreakEven(b: BreakEven): { value: string; note: string } {
    if (b.kind === 'immediate') {
        return {
            value: 'At conversion',
            note: 'Customer cash by conversion already covers modeled deploy spend.',
        };
    }
    if (b.kind === 'never') {
        return {
            value: '—',
            note: 'No recurring cash path recovers deploy spend from this quote.',
        };
    }
    return {
        value: `${b.monthsAfterStart} mo`,
        note: 'Months from signed pilot until cumulative customer cash covers gross deploy spend.',
    };
}

function pctDisplay(fraction: number): number {
    return Math.round(fraction * 1000) / 10;
}

function discountSuffix(fraction: number): string {
    if (fraction <= 0) return '';
    return ` · ${pctDisplay(fraction)}% off`;
}

function ScenarioTermsSummary({ params }: { params: VenuePricingParams }) {
    const isSteadyState = params.pilotDurationDays <= 0;
    const extras = Math.max(0, params.goForwardDeviceCount - params.pilotDeviceCount);

    const hardware =
        params.hardwareMode === 'lease'
            ? `${currencyDetail(params.hardwareLeasePerMonth)}/device${discountSuffix(params.hardwareDiscountFraction)}`
            : `${currency(params.hardwarePurchase)}${discountSuffix(params.hardwareDiscountFraction)}`;
    const software =
        params.softwareCadence === 'year'
            ? `${currency(params.softwareYearly)}${discountSuffix(params.softwareDiscountFraction)}`
            : `${currencyDetail(params.softwareMonthly)}${discountSuffix(params.softwareDiscountFraction)}`;

    let goForward = `${params.goForwardDeviceCount} devices`;
    if (extras > 0) {
        goForward +=
            params.expansionInstallTiming === 'return_trip'
                ? ` · ${extras} added on return trip`
                : ` · ${extras} added during pilot`;
    }
    if (params.additionalGoForwardLocations > 0) {
        const n = params.additionalGoForwardLocations;
        goForward += ` · ${n} additional location${n === 1 ? '' : 's'}`;
    }

    const rows: { label: string; value: string }[] = [
        {
            label: params.hardwareMode === 'lease' ? 'Hardware lease per month' : 'Hardware purchase per device',
            value: hardware,
        },
        {
            label: 'Installation fee per trip',
            value: `${currency(params.installationFeePerTrip)}${discountSuffix(params.installationDiscountFraction)}`,
        },
        {
            label: params.softwareCadence === 'year' ? 'Software per device / year' : 'Software per device / month',
            value: software,
        },
        {
            label: 'Pilot',
            value: isSteadyState
                ? 'None (steady-state)'
                : `${params.pilotDurationDays} days · ${params.pilotDeviceCount} devices · ${currency(params.pilotPackagePrice)} package`,
        },
        ...(isSteadyState ? [] : [{ label: 'Conversion credit', value: currency(params.conversionCredit) }]),
        { label: 'Go-forward', value: goForward },
        {
            label: 'Watchly COGS',
            value: `${currency(params.deviceHardwareCost)} hardware + ${currency(params.deviceInstallationLaborCost)} labor / device`,
        },
    ];

    return (
        <Stack gap={4} mb="md">
            {rows.map(row => (
                <Group key={row.label} justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                        {row.label}
                    </Text>
                    <Text size="xs" ta="right">
                        {row.value}
                    </Text>
                </Group>
            ))}
        </Stack>
    );
}

function feasibleNumberProps(
    key: SweepableVenueParam,
    feasibleByKey: Partial<Record<SweepableVenueParam, FeasibleInputSet>>,
    fallback: { min?: number; max?: number },
    asPercent?: boolean,
    current?: number
): { min?: number; max?: number; hint?: string } {
    const set = feasibleByKey[key];
    if (!set || set.kind === 'unconstrained' || set.kind === 'all') {
        return fallback;
    }
    if (set.kind === 'empty') {
        return {
            ...fallback,
            hint: 'No value of this input satisfies the goals at the current quote.',
        };
    }
    if (set.kind === 'union') {
        return {
            ...fallback,
            hint: 'Feasible in disconnected ranges — this field is not clamped.',
        };
    }
    if (set.clamp) {
        const scale = asPercent ? 100 : 1;
        const currentScaled = current == null ? undefined : current * scale;
        const inside =
            currentScaled == null || (currentScaled >= set.clamp.min * scale && currentScaled <= set.clamp.max * scale);
        if (!inside) {
            return {
                ...fallback,
                hint: 'Current value is outside the feasible range for these goals.',
            };
        }
        const min = Math.max(fallback.min ?? Number.NEGATIVE_INFINITY, set.clamp.min * scale);
        const max = Math.min(fallback.max ?? Number.POSITIVE_INFINITY, set.clamp.max * scale);
        return { min, max };
    }
    return fallback;
}

function InvoiceLineItems({ invoice }: { invoice: CustomerInvoice }) {
    if (invoice.lineItems.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                No charges on this invoice.
            </Text>
        );
    }
    return (
        <Stack gap="xs">
            {invoice.lineItems.map(item => (
                <Box key={item.label}>
                    <Group justify="space-between" wrap="nowrap" gap="md">
                        <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                            {item.label}
                            {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                        </Text>
                        <Text fw={500} style={{ whiteSpace: 'nowrap' }}>
                            {currencyDetail(item.amountAfterCredit)}
                        </Text>
                    </Group>
                    {(item.discountFraction > 0 || item.amountBeforeCredit !== item.amountAfterCredit) && (
                        <Text size="xs" c="dimmed">
                            List {currencyDetail(item.listUnit)}
                            {item.discountFraction > 0 ? ` · ${pctDisplay(item.discountFraction)}% off` : ''}
                            {item.amountBeforeCredit !== item.amountAfterCredit
                                ? `· before credit ${currencyDetail(item.amountBeforeCredit)}`
                                : ''}
                        </Text>
                    )}
                </Box>
            ))}
        </Stack>
    );
}

function CustomerInvoiceCard({ invoice }: { invoice: CustomerInvoice | null }) {
    if (!invoice) {
        return (
            <Card withBorder padding="lg" radius="md" shadow="sm" h="100%">
                <Text fw={600} mb="xs">
                    Conversion invoice
                </Text>
                <Text size="sm" c="dimmed">
                    No conversion invoice — there is no pilot window, so Venue charges start on the signup invoice.
                </Text>
            </Card>
        );
    }

    return (
        <Card withBorder padding="lg" radius="md" shadow="sm" h="100%">
            <Group justify="space-between" mb="xs" wrap="wrap" gap="xs">
                <Text fw={600}>{invoice.title}</Text>
                <Badge variant="light" color="indigo">
                    {currency(invoice.total)}
                </Badge>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
                {invoice.description}
            </Text>
            <InvoiceLineItems invoice={invoice} />
            {invoice.creditApplied > 0 && (
                <Text size="xs" c="dimmed" mt="sm">
                    Pilot credit applied {currency(invoice.creditApplied)}
                    {invoice.creditLeftover > 0 ? ` · leftover ${currency(invoice.creditLeftover)} (not refunded)` : ''}
                    .
                </Text>
            )}
            <Divider my="md" />
            <Group justify="space-between">
                <Text size="sm" fw={600}>
                    Amount due
                </Text>
                <Text fw={700} size="lg">
                    {currency(invoice.total)}
                </Text>
            </Group>
        </Card>
    );
}

const NET_PLOT_W = 720;
const NET_PLOT_H = 320;
const NET_PLOT_PAD = { top: 32, right: 36, bottom: 48, left: 64 };

function formatNetAxisCurrency(n: number): string {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}k`;
    return `${sign}$${Math.round(abs)}`;
}

function WatchlyNetProfitPlot({ points }: { points: WatchlyNetProfitPoint[] }) {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme();
    const lineColor = theme.colors.teal[6];
    const zeroColor = colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
    const gridColor = colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];
    const labelColor = colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[7];

    if (points.length < 2) {
        return (
            <Text size="sm" c="dimmed">
                Not enough data to plot.
            </Text>
        );
    }

    const innerW = NET_PLOT_W - NET_PLOT_PAD.left - NET_PLOT_PAD.right;
    const innerH = NET_PLOT_H - NET_PLOT_PAD.top - NET_PLOT_PAD.bottom;

    const xMax = points[points.length - 1].month;
    const rawMin = Math.min(...points.map(p => p.netProfit));
    const rawMax = Math.max(...points.map(p => p.netProfit));
    const pad = Math.max(1, (rawMax - rawMin) * 0.08);
    let yMin = Math.min(0, rawMin) - (rawMin < 0 ? pad : 0);
    let yMax = Math.max(0, rawMax) + (rawMax > 0 ? pad : 0);
    if (yMin === yMax) {
        yMin -= 1;
        yMax += 1;
    }

    const xScale = (m: number) => NET_PLOT_PAD.left + (m / (xMax || 1)) * innerW;
    const yScale = (v: number) => NET_PLOT_PAD.top + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.month)} ${yScale(p.netProfit)}`).join(' ');

    const yTicks = 4;
    const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) => yMin + ((yMax - yMin) * i) / yTicks);

    const xTickMonths = [0, 12, 24, 36, 48, 60].filter(m => m <= xMax);
    const yearPoints = xTickMonths
        .map(xm => points.find(p => p.month === xm))
        .filter((p): p is WatchlyNetProfitPoint => p != null);

    return (
        <svg
            width="100%"
            height={NET_PLOT_H}
            viewBox={`0 0 ${NET_PLOT_W} ${NET_PLOT_H}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={`Line chart of Watchly cumulative net profit over ${xMax / 12} years.`}
        >
            <title>Watchly net profit over time</title>
            {yTickVals.map((yv, i) => {
                const y = yScale(yv);
                return (
                    <g key={`y-${i}`}>
                        <line
                            x1={NET_PLOT_PAD.left}
                            x2={NET_PLOT_W - NET_PLOT_PAD.right}
                            y1={y}
                            y2={y}
                            stroke={gridColor}
                            strokeWidth={1}
                            strokeOpacity={0.5}
                        />
                        <text x={NET_PLOT_PAD.left - 8} y={y + 4} textAnchor="end" fill={labelColor} fontSize={11}>
                            {formatNetAxisCurrency(yv)}
                        </text>
                    </g>
                );
            })}
            <line
                x1={NET_PLOT_PAD.left}
                x2={NET_PLOT_W - NET_PLOT_PAD.right}
                y1={yScale(0)}
                y2={yScale(0)}
                stroke={zeroColor}
                strokeWidth={1.5}
                strokeDasharray="4 3"
            />
            {xTickMonths.map(xm => (
                <text
                    key={`x-${xm}`}
                    x={xScale(xm)}
                    y={NET_PLOT_H - 12}
                    textAnchor="middle"
                    fill={labelColor}
                    fontSize={11}
                >
                    {xm / 12}
                </text>
            ))}
            <path d={linePath} fill="none" stroke={lineColor} strokeWidth={2.5} strokeLinejoin="round" />
            {yearPoints.map((p, i) => {
                const cx = xScale(p.month);
                const cy = yScale(p.netProfit);
                const isFirst = i === 0;
                const isLast = i === yearPoints.length - 1;
                const labelAbove = cy > NET_PLOT_PAD.top + 14;
                return (
                    <g key={`year-${p.month}`}>
                        <circle cx={cx} cy={cy} r={4} fill={lineColor} stroke="white" strokeWidth={2} />
                        <text
                            x={isFirst ? cx + 8 : isLast ? cx - 8 : cx}
                            y={labelAbove ? cy - 10 : cy + 16}
                            textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
                            fill={labelColor}
                            fontSize={11}
                            fontWeight={600}
                        >
                            {currency(p.netProfit)}
                        </text>
                    </g>
                );
            })}
            <text
                x={NET_PLOT_W / 2}
                y={NET_PLOT_H - 2}
                textAnchor="middle"
                fill={labelColor}
                fontSize={12}
                fontWeight={500}
            >
                Years
            </text>
            <text
                x={18}
                y={NET_PLOT_PAD.top + innerH / 2}
                textAnchor="middle"
                fill={labelColor}
                fontSize={12}
                fontWeight={500}
                transform={`rotate(-90, 18, ${NET_PLOT_PAD.top + innerH / 2})`}
            >
                Watchly net profit
            </text>
        </svg>
    );
}

const VenuePricing: React.FC<{ showHeader?: boolean }> = ({ showHeader = true }) => {
    const [params, setParams] = useState<VenuePricingParams>(cloneDefaults);

    const [bounds, setBounds] = useState<OutputBounds>({});

    const patch = useCallback(<K extends keyof VenuePricingParams>(key: K, value: VenuePricingParams[K]) => {
        setParams(p => ({ ...p, [key]: value }));
    }, []);

    const patchPct = useCallback(
        (
            key: 'hardwareDiscountFraction' | 'installationDiscountFraction' | 'softwareDiscountFraction',
            value: number | string
        ) => {
            const n = typeof value === 'number' ? value : parseFloat(String(value));
            const pctClean = Number.isFinite(n) ? n : 0;
            const fraction = Math.min(1, Math.max(0, pctClean / 100));
            setParams(p => ({ ...p, [key]: fraction }));
        },
        []
    );

    const resetDefaults = useCallback(() => setParams(cloneDefaults()), []);
    const loadSteadyState = useCallback(() => setParams({ ...STEADY_STATE_VENUE_PRICING }), []);

    const breakdown = useMemo(() => computeVenuePricing(params), [params]);
    const currentMetrics = useMemo(() => metricsFromBreakdown(breakdown), [breakdown]);
    const sweeps = useMemo(() => sweepActiveVenueParams(params), [params]);
    const feasibleByKey = useMemo(() => {
        const map: Partial<Record<SweepableVenueParam, FeasibleInputSet>> = {};
        for (const sweep of sweeps) {
            map[sweep.key] = feasibleInputSet(sweep, bounds);
        }
        return map;
    }, [sweeps, bounds]);
    const breakEvenDisplay = formatBreakEven(breakdown.breakEvenFromStart);
    const incrementalBe = formatBreakEven(breakdown.incrementalNextScreen.monthsToRecover);
    const isSteadyState = params.pilotDurationDays <= 0;
    const hwCost = feasibleNumberProps(
        'deviceHardwareCost',
        feasibleByKey,
        { min: 0 },
        false,
        params.deviceHardwareCost
    );
    const laborCost = feasibleNumberProps(
        'deviceInstallationLaborCost',
        feasibleByKey,
        { min: 0 },
        false,
        params.deviceInstallationLaborCost
    );
    const lease = feasibleNumberProps(
        'hardwareLeasePerMonth',
        feasibleByKey,
        { min: 0 },
        false,
        params.hardwareLeasePerMonth
    );
    const purchase = feasibleNumberProps('hardwarePurchase', feasibleByKey, { min: 0 }, false, params.hardwarePurchase);
    const hwDisc = feasibleNumberProps(
        'hardwareDiscountFraction',
        feasibleByKey,
        { min: 0, max: 100 },
        true,
        params.hardwareDiscountFraction
    );
    const installFee = feasibleNumberProps(
        'installationFeePerTrip',
        feasibleByKey,
        { min: 0 },
        false,
        params.installationFeePerTrip
    );
    const installDisc = feasibleNumberProps(
        'installationDiscountFraction',
        feasibleByKey,
        { min: 0, max: 100 },
        true,
        params.installationDiscountFraction
    );
    const softwareMo = feasibleNumberProps('softwareMonthly', feasibleByKey, { min: 0 }, false, params.softwareMonthly);
    const softwareYr = feasibleNumberProps('softwareYearly', feasibleByKey, { min: 0 }, false, params.softwareYearly);
    const softwareDisc = feasibleNumberProps(
        'softwareDiscountFraction',
        feasibleByKey,
        { min: 0, max: 100 },
        true,
        params.softwareDiscountFraction
    );
    const pilotDays = feasibleNumberProps(
        'pilotDurationDays',
        feasibleByKey,
        { min: 0, max: 365 },
        false,
        params.pilotDurationDays
    );
    const pilotCount = feasibleNumberProps(
        'pilotDeviceCount',
        feasibleByKey,
        { min: 0, max: 500 },
        false,
        params.pilotDeviceCount
    );
    const packagePrice = feasibleNumberProps(
        'pilotPackagePrice',
        feasibleByKey,
        { min: 0 },
        false,
        params.pilotPackagePrice
    );
    const credit = feasibleNumberProps('conversionCredit', feasibleByKey, { min: 0 }, false, params.conversionCredit);
    const goForward = feasibleNumberProps(
        'goForwardDeviceCount',
        feasibleByKey,
        {
            min: params.pilotDeviceCount,
            max: 500,
        },
        false,
        params.goForwardDeviceCount
    );
    const extraLocations = feasibleNumberProps(
        'additionalGoForwardLocations',
        feasibleByKey,
        { min: 0, max: 50 },
        false,
        params.additionalGoForwardLocations
    );

    return (
        <Box h={{ md: '100%' }} style={{ minHeight: 0, overflow: 'hidden' }}>
            {showHeader && (
                <Group justify="space-between" align="flex-start" wrap="wrap" mb="md">
                    <div>
                        <Group gap="sm" mb="xs">
                            <ThemeIcon
                                size="lg"
                                radius="md"
                                variant="gradient"
                                gradient={{ from: 'indigo', to: 'cyan' }}
                            >
                                <IconCalculator size={22} stroke={1.5} />
                            </ThemeIcon>
                            <Title order={2}>Pricing | Venue</Title>
                        </Group>
                        <Text c="dimmed" size="sm" maw={640}>
                            Venue offer and Pilot → convert → expand explorer
                        </Text>
                    </div>
                </Group>
            )}

            <Flex direction={{ base: 'column', md: 'row' }} gap="lg" h={{ md: '100%' }} style={{ minHeight: 0 }}>
                <Box
                    w={{ md: 340, lg: 380, xl: 420 }}
                    miw={{ md: 280 }}
                    h={{ md: '100%' }}
                    style={{ overflowY: 'auto', flexShrink: 0, minHeight: 0 }}
                    pr={{ md: 'xs' }}
                >
                    <Stack gap="md">
                        <Group>
                            <Button
                                variant="light"
                                size="xs"
                                leftSection={<IconRefresh size={14} />}
                                onClick={resetDefaults}
                            >
                                Reset to pilot defaults
                            </Button>
                            <Button variant="subtle" size="xs" onClick={loadSteadyState}>
                                Load steady-state (no pilot)
                            </Button>
                        </Group>

                        <Stack gap="md">
                            <Card withBorder padding="lg" radius="md" shadow="sm">
                                <Text fw={600} mb="xs">
                                    Watchly costs
                                </Text>
                                <Text size="sm" c="dimmed" mb="md">
                                    Internal COGS for hardware and install labor. Used for capital and break-even — not
                                    shown on the customer invoice.
                                </Text>
                                <Stack gap="md">
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Device hardware cost to Watchly
                                        </Text>
                                        <NumberInput
                                            min={hwCost.min}
                                            max={hwCost.max}
                                            prefix="$"
                                            thousandSeparator=","
                                            value={params.deviceHardwareCost}
                                            onChange={v => patch('deviceHardwareCost', typeof v === 'number' ? v : 0)}
                                        />
                                        {hwCost.hint && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                {hwCost.hint}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Installation labor per device
                                        </Text>
                                        <NumberInput
                                            min={laborCost.min}
                                            max={laborCost.max}
                                            prefix="$"
                                            thousandSeparator=","
                                            value={params.deviceInstallationLaborCost}
                                            onChange={v =>
                                                patch('deviceInstallationLaborCost', typeof v === 'number' ? v : 0)
                                            }
                                        />
                                        {laborCost.hint && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                {laborCost.hint}
                                            </Text>
                                        )}
                                    </Box>
                                </Stack>
                            </Card>

                            <Card withBorder padding="lg" radius="md" shadow="sm">
                                <Text fw={600} mb="xs">
                                    Prices charged to customers
                                </Text>
                                <Text size="sm" c="dimmed" mb="md">
                                    Post-pilot Venue rates. Installation is billed per Watchly trip to the venue (any
                                    number of devices on that visit).
                                </Text>
                                <Stack gap="md">
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Hardware
                                        </Text>
                                        <SegmentedControl
                                            fullWidth
                                            value={params.hardwareMode}
                                            onChange={v => patch('hardwareMode', v as HardwareMode)}
                                            data={[
                                                { label: 'Lease', value: 'lease' },
                                                { label: 'Purchase', value: 'purchase' },
                                            ]}
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            {params.hardwareMode === 'lease'
                                                ? 'Monthly lease is paid indefinitely. Malfunctioning hardware is replaced at no additional cost to customer.'
                                                : "Purchase price is paid once up front. Repair or replacement is the customer's responsibility after a 90 day warranty period."}
                                        </Text>
                                    </Box>
                                    {params.hardwareMode === 'lease' ? (
                                        <Box>
                                            <Text size="sm" fw={500} mb={6}>
                                                Hardware lease per month
                                            </Text>
                                            <NumberInput
                                                min={lease.min}
                                                max={lease.max}
                                                prefix="$"
                                                thousandSeparator=","
                                                value={params.hardwareLeasePerMonth}
                                                onChange={v =>
                                                    patch('hardwareLeasePerMonth', typeof v === 'number' ? v : 0)
                                                }
                                            />
                                            {lease.hint && (
                                                <Text size="xs" c="dimmed" mt={4}>
                                                    {lease.hint}
                                                </Text>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Text size="sm" fw={500} mb={6}>
                                                Hardware purchase per device
                                            </Text>
                                            <NumberInput
                                                min={purchase.min}
                                                max={purchase.max}
                                                prefix="$"
                                                thousandSeparator=","
                                                value={params.hardwarePurchase}
                                                onChange={v => patch('hardwarePurchase', typeof v === 'number' ? v : 0)}
                                            />
                                            {purchase.hint && (
                                                <Text size="xs" c="dimmed" mt={4}>
                                                    {purchase.hint}
                                                </Text>
                                            )}
                                        </Box>
                                    )}
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Discount on hardware
                                        </Text>
                                        <NumberInput
                                            min={hwDisc.min}
                                            max={hwDisc.max}
                                            decimalScale={1}
                                            suffix="%"
                                            value={pctDisplay(params.hardwareDiscountFraction)}
                                            onChange={v => patchPct('hardwareDiscountFraction', v)}
                                        />
                                        {hwDisc.hint && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                {hwDisc.hint}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Installation fee per trip
                                        </Text>
                                        <NumberInput
                                            min={installFee.min}
                                            max={installFee.max}
                                            prefix="$"
                                            thousandSeparator=","
                                            value={params.installationFeePerTrip}
                                            onChange={v =>
                                                patch('installationFeePerTrip', typeof v === 'number' ? v : 0)
                                            }
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            Pilot trip is included in the package. A later return trip to add devices
                                            bills this again.
                                            {installFee.hint ? ` ${installFee.hint}` : ''}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Discount on installation
                                        </Text>
                                        <NumberInput
                                            min={installDisc.min}
                                            max={installDisc.max}
                                            decimalScale={1}
                                            suffix="%"
                                            value={pctDisplay(params.installationDiscountFraction)}
                                            onChange={v => patchPct('installationDiscountFraction', v)}
                                        />
                                        {installDisc.hint && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                {installDisc.hint}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Software subscription
                                        </Text>
                                        <SegmentedControl
                                            fullWidth
                                            value={params.softwareCadence}
                                            onChange={v => patch('softwareCadence', v as SoftwareCadence)}
                                            data={[
                                                { label: 'Monthly', value: 'month' },
                                                { label: 'Yearly', value: 'year' },
                                            ]}
                                        />
                                    </Box>
                                    {params.softwareCadence === 'month' ? (
                                        <Box>
                                            <Text size="sm" fw={500} mb={6}>
                                                Software per device / month
                                            </Text>
                                            <NumberInput
                                                min={softwareMo.min}
                                                max={softwareMo.max}
                                                prefix="$"
                                                thousandSeparator=","
                                                value={params.softwareMonthly}
                                                onChange={v => patch('softwareMonthly', typeof v === 'number' ? v : 0)}
                                            />
                                            {softwareMo.hint && (
                                                <Text size="xs" c="dimmed" mt={4}>
                                                    {softwareMo.hint}
                                                </Text>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Text size="sm" fw={500} mb={6}>
                                                Software per device / year
                                            </Text>
                                            <NumberInput
                                                min={softwareYr.min}
                                                max={softwareYr.max}
                                                prefix="$"
                                                thousandSeparator=","
                                                value={params.softwareYearly}
                                                onChange={v => patch('softwareYearly', typeof v === 'number' ? v : 0)}
                                            />
                                            {softwareYr.hint && (
                                                <Text size="xs" c="dimmed" mt={4}>
                                                    {softwareYr.hint}
                                                </Text>
                                            )}
                                        </Box>
                                    )}
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Discount on software
                                        </Text>
                                        <NumberInput
                                            min={softwareDisc.min}
                                            max={softwareDisc.max}
                                            decimalScale={1}
                                            suffix="%"
                                            value={pctDisplay(params.softwareDiscountFraction)}
                                            onChange={v => patchPct('softwareDiscountFraction', v)}
                                        />
                                        {softwareDisc.hint && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                {softwareDisc.hint}
                                            </Text>
                                        )}
                                    </Box>
                                </Stack>
                            </Card>

                            <Card withBorder padding="lg" radius="md" shadow="sm">
                                <Group justify="space-between" mb="xs">
                                    <Text fw={600}>Pilot → convert → expand</Text>
                                    {isSteadyState && (
                                        <Badge size="sm" variant="light" color="gray">
                                            Steady-state
                                        </Badge>
                                    )}
                                </Group>
                                <Text size="sm" c="dimmed" mb="md">
                                    Package for the pilot window, conversion credit, and same-location expansion. Set
                                    pilot duration to 0 for a steady-state Venue quote with no package.
                                </Text>
                                <Stack gap="md">
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Pilot duration (days)
                                        </Text>
                                        <NumberInput
                                            min={pilotDays.min}
                                            max={pilotDays.max}
                                            value={params.pilotDurationDays}
                                            onChange={v => patch('pilotDurationDays', typeof v === 'number' ? v : 0)}
                                        />
                                        {pilotDays.hint && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                {pilotDays.hint}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Pilot device count
                                        </Text>
                                        <NumberInput
                                            min={pilotCount.min}
                                            max={pilotCount.max}
                                            value={params.pilotDeviceCount}
                                            onChange={v => patch('pilotDeviceCount', typeof v === 'number' ? v : 0)}
                                        />
                                        {pilotCount.hint && (
                                            <Text size="xs" c="dimmed" mt={4}>
                                                {pilotCount.hint}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Pilot package price
                                        </Text>
                                        <NumberInput
                                            min={packagePrice.min}
                                            max={packagePrice.max}
                                            prefix="$"
                                            thousandSeparator=","
                                            value={params.pilotPackagePrice}
                                            onChange={v => patch('pilotPackagePrice', typeof v === 'number' ? v : 0)}
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            All-inclusive for the window: loaned hardware, install, software $0. The
                                            pilot install trip is not re-billed at conversion.
                                            {packagePrice.hint ? ` ${packagePrice.hint}` : ''}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Conversion credit
                                        </Text>
                                        <NumberInput
                                            min={credit.min}
                                            max={credit.max}
                                            prefix="$"
                                            thousandSeparator=","
                                            value={params.conversionCredit}
                                            onChange={v => patch('conversionCredit', typeof v === 'number' ? v : 0)}
                                            disabled={isSteadyState}
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            Applied in order: hardware purchase → 12-month software term → 12-month
                                            lease.
                                            {credit.hint ? ` ${credit.hint}` : ''}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Go-forward device count
                                        </Text>
                                        <NumberInput
                                            min={goForward.min}
                                            max={goForward.max}
                                            value={params.goForwardDeviceCount}
                                            onChange={v =>
                                                patch(
                                                    'goForwardDeviceCount',
                                                    typeof v === 'number' ? v : params.pilotDeviceCount
                                                )
                                            }
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            Must be ≥ pilot count. Extras installed during the pilot visit do not
                                            re-bill install; a return trip does.
                                            {goForward.hint ? ` ${goForward.hint}` : ''}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Additional devices installed
                                        </Text>
                                        <SegmentedControl
                                            fullWidth
                                            value={params.expansionInstallTiming}
                                            onChange={v => patch('expansionInstallTiming', v as ExpansionInstallTiming)}
                                            data={[
                                                { label: 'During pilot', value: 'during_pilot' },
                                                { label: 'Return trip', value: 'return_trip' },
                                            ]}
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            Return trip: customer pays another install-trip fee; Watchly pays labor
                                            again on extras.
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text size="sm" fw={500} mb={6}>
                                            Additional go-forward locations
                                        </Text>
                                        <NumberInput
                                            min={extraLocations.min}
                                            max={extraLocations.max}
                                            value={params.additionalGoForwardLocations}
                                            onChange={v =>
                                                patch('additionalGoForwardLocations', typeof v === 'number' ? v : 0)
                                            }
                                        />
                                        <Text size="xs" c="dimmed" mt={4}>
                                            Extra venues standing up at conversion, beyond the pilot location. Each
                                            bills the install-trip fee once. Conversion credit does not cover it.
                                            {extraLocations.hint ? ` ${extraLocations.hint}` : ''}
                                        </Text>
                                    </Box>
                                </Stack>
                            </Card>
                        </Stack>
                    </Stack>
                </Box>

                <Box h={{ md: '100%' }} style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto' }}>
                    <Stack gap="lg">
                        <Card withBorder padding="lg" radius="md" shadow="sm">
                            <Group justify="space-between" mb="xs">
                                <Text fw={600}>Scenario Summary</Text>
                                <Badge variant="light" color="indigo">
                                    Live
                                </Badge>
                            </Group>
                            <Text size="sm" c="dimmed" mb="sm">
                                At go-forward count ({breakdown.goForwardDeviceCount} devices) after credits.
                            </Text>
                            <ScenarioTermsSummary params={params} />
                            <Divider mb="md" />
                            <Stack gap="sm">
                                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                                    <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                                        Gross deploy spend
                                    </Text>
                                    <Text fw={600} style={{ whiteSpace: 'nowrap' }}>
                                        {currency(breakdown.grossDeploySpend)}
                                    </Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    (Hardware + labor) × go-forward devices
                                    {params.expansionInstallTiming === 'return_trip' &&
                                    breakdown.expansionDeviceCount > 0
                                        ? ', plus return-trip labor on extras'
                                        : ''}
                                    .
                                </Text>

                                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                                    <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                                        Capital tied up during pilot
                                    </Text>
                                    <Text fw={600} style={{ whiteSpace: 'nowrap' }}>
                                        {currency(breakdown.capitalTiedUpDuringPilot)}
                                    </Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    (Hardware + labor) × pilot devices minus the pilot package, for{' '}
                                    {params.pilotDurationDays} days.
                                </Text>

                                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                                    <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                                        Capital tied up post-pilot
                                    </Text>
                                    <Text fw={600} style={{ whiteSpace: 'nowrap' }}>
                                        {currency(breakdown.upFrontCapitalStillOnWatchly)}
                                    </Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Gross deploy spend minus the pilot package and conversion invoice cash that offsets
                                    capital (hardware purchase after credit + return-trip or additional-location install
                                    if any). Lease and software do not offset.
                                </Text>

                                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                                    <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                                        Time to break even from start
                                    </Text>
                                    <Text fw={600} style={{ whiteSpace: 'nowrap' }}>
                                        {breakEvenDisplay.value}
                                    </Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    {breakEvenDisplay.note}
                                </Text>

                                <Divider my="xs" />

                                <Group justify="space-between">
                                    <Text size="sm">Monthly recurring revenue</Text>
                                    <Text fw={600}>{currencyDetail(breakdown.mrr)}/mo</Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Discounted lease (if leasing) + software as a monthly equivalent.
                                </Text>
                                <Group justify="space-between">
                                    <Text size="sm">Annual recurring revenue</Text>
                                    <Text fw={600}>{currency(breakdown.arr)}</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text size="sm">Cumulative contract value after year 1</Text>
                                    <Text fw={600}>{currency(breakdown.yearOneCustomerTotal)}</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text size="sm">Cumulative contract value after year 2</Text>
                                    <Text fw={600}>
                                        {currency(breakdown.yearOneCustomerTotal + breakdown.yearTwoCustomerTotal)}
                                    </Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Total customer cash through the end of that contract term (package plus each
                                    12-month Venue invoice). Year two is the next 12-month renewal — no package,
                                    install, or repurchase.
                                </Text>

                                <Divider my="xs" />

                                <Text fw={600} size="sm">
                                    Incremental next screen (same location)
                                </Text>
                                <Group justify="space-between">
                                    <Text size="sm">Customer up-front</Text>
                                    <Text fw={500}>{currency(breakdown.incrementalNextScreen.customerUpFront)}</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text size="sm">Customer monthly</Text>
                                    <Text fw={500}>
                                        {currencyDetail(breakdown.incrementalNextScreen.customerMonthlyRecurring)}/mo
                                    </Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text size="sm">Watchly cost</Text>
                                    <Text fw={500}>{currency(breakdown.incrementalNextScreen.watchlyCost)}</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text size="sm">Months to recover</Text>
                                    <Text fw={500}>{incrementalBe.value}</Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Adding one more device later is a return trip: customer pays hardware (if purchase)
                                    + install-trip fee + software. Watchly pays one more hardware + labor.
                                </Text>
                            </Stack>
                        </Card>

                        <Stack gap="md">
                            <div>
                                <Text fw={600}>Customer invoices</Text>
                                <Text size="sm" c="dimmed" maw={720}>
                                    Three invoices a venue would actually receive: due at signup, due at conversion
                                    (the first 12-month contract, for pilot customers), and the next 12-month renewal
                                    after that.
                                </Text>
                            </div>
                            <SimpleGrid cols={{ base: 1, xl: 3 }} spacing="lg">
                                <CustomerInvoiceCard invoice={breakdown.invoices.signup} />
                                <CustomerInvoiceCard invoice={breakdown.invoices.conversion} />
                                <CustomerInvoiceCard invoice={breakdown.invoices.ongoing} />
                            </SimpleGrid>
                            <Card withBorder padding="lg" radius="md" shadow="sm">
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                                    <Box>
                                        <Group justify="space-between">
                                            <Text size="sm">What the customer pays in year one</Text>
                                            <Text fw={700} size="lg">
                                                {currency(breakdown.yearOneCustomerTotal)}
                                            </Text>
                                        </Group>
                                        <Text size="xs" c="dimmed" mt={4}>
                                            {isSteadyState
                                                ? 'The signup invoice: install, hardware, and the first 12-month Venue contract.'
                                                : `Pilot package plus the 12-month contract that starts when the ${params.pilotDurationDays}-day pilot ends.`}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Group justify="space-between">
                                            <Text size="sm">What the customer pays in year two</Text>
                                            <Text fw={700} size="lg">
                                                {currency(breakdown.yearTwoCustomerTotal)}
                                            </Text>
                                        </Group>
                                        <Text size="xs" c="dimmed" mt={4}>
                                            The next 12-month contract at go-forward count — no package, install, or
                                            repurchase.
                                        </Text>
                                    </Box>
                                </SimpleGrid>
                            </Card>
                        </Stack>

                        {!isSteadyState && (
                            <Card withBorder padding="lg" radius="md" shadow="sm">
                                <Text fw={600} mb="xs">
                                    Scenario comparison
                                </Text>
                                <Text size="sm" c="dimmed" mb="md">
                                    Same rates and credits. Use this to see whether a conversion credit or putting
                                    extras in on the first visit actually pulls the venue forward.
                                </Text>
                                <Table.ScrollContainer minWidth={640}>
                                    <Table striped highlightOnHover withTableBorder>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Scenario</Table.Th>
                                                <Table.Th ta="right">Devices</Table.Th>
                                                <Table.Th ta="right">Customer cash (Y1)</Table.Th>
                                                <Table.Th ta="right">Watchly net (Y1)</Table.Th>
                                                <Table.Th ta="right">MRR after</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {breakdown.scenarios.map(s => (
                                                <Table.Tr key={s.id}>
                                                    <Table.Td>
                                                        <Text size="sm" fw={500}>
                                                            {s.label}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td ta="right">{s.deviceCount}</Table.Td>
                                                    <Table.Td ta="right">
                                                        {currency(s.customerCashThroughYearOne)}
                                                    </Table.Td>
                                                    <Table.Td ta="right">
                                                        {currency(s.watchlyNetCashThroughYearOne)}
                                                    </Table.Td>
                                                    <Table.Td ta="right">
                                                        {currencyDetail(s.mrrAfterDecision)}/mo
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>
                                <Text size="xs" c="dimmed" mt="md">
                                    Walk away: customer spent only the package; Watchly keeps package cash and recovers
                                    hardware, but labor is sunk. Convert / expand: hardware stays in the field; net ≈
                                    customer year-one cash minus deploy spend.
                                </Text>
                            </Card>
                        )}

                        <Card withBorder padding="lg" radius="md" shadow="sm">
                            <Group justify="space-between" mb="xs" wrap="wrap">
                                <Text fw={600}>Watchly net profit over time</Text>
                                <Badge variant="light" color="teal">
                                    {NET_PROFIT_HORIZON_MONTHS / 12} years
                                </Badge>
                            </Group>
                            <Text size="sm" c="dimmed" mb="lg">
                                Cumulative cash in (pilot package, conversion after credit, return-trip install, then
                                recurring lease/software) minus deploy spend (hardware + labor) for the
                                convert-and-expand path at go-forward count. Dashed line is break-even ($0).
                            </Text>
                            <Box style={{ width: '100%', maxWidth: 780 }}>
                                <WatchlyNetProfitPlot points={breakdown.watchlyNetProfitSeries} />
                            </Box>
                            <Text size="xs" c="dimmed" mt="md">
                                Month 0 is install / pilot start. Conversion lands at month{' '}
                                {params.pilotDurationDays <= 0 ? 0 : Math.ceil(params.pilotDurationDays / 30)}. End of
                                horizon:{' '}
                                {currency(
                                    breakdown.watchlyNetProfitSeries[breakdown.watchlyNetProfitSeries.length - 1]
                                        ?.netProfit ?? 0
                                )}
                                .
                            </Text>
                        </Card>

                        <VenueSensitivityExplorer
                            params={params}
                            mrr={breakdown.mrr}
                            patch={patch}
                            bounds={bounds}
                            onBoundsChange={setBounds}
                            sweeps={sweeps}
                            currentMetrics={currentMetrics}
                            feasibleByKey={feasibleByKey}
                        />
                    </Stack>
                </Box>
            </Flex>
        </Box>
    );
};

export default VenuePricing;
